import { Navbar, Nav, Container, Offcanvas } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LuHeart,
  LuShoppingBag,
  LuBell,
  LuUser,
  LuLogOut,
} from "react-icons/lu";
import { useEffect, useRef, useState } from "react";
import { RiArrowDropDownLine } from "react-icons/ri";
import { useAuth } from "../context/AuthContext";
import api from "../api/axios";
import logo from "../images/logo.webp";

import {
  FiHome,
  FiShoppingBag,
  FiHelpCircle,
  FiPhone,
  FiUser,
  FiLogIn,
  FiLogOut,
  FiChevronDown,
  FiChevronRight
} from "react-icons/fi";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout, authLoading } = useAuth();


  const [showHeader, setShowHeader] = useState(true);
  const lastScrollYRef = useRef(0);
  const scrollFrameRef = useRef(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [cartCount, setCartCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);

  const userImage = "https://i.pravatar.cc/80?img=47";

  const [showSidebar, setShowSidebar] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [openSubMenu, setOpenSubMenu] = useState(null);

  const handleMobileNavigate = (path, options = {}) => {
    setShowSidebar(false);
    setOpenMenu(null);
    setOpenSubMenu(null);

    navigate(path, options);
  };

  const fetchCartCount = async () => {
    try {
      if (!isAuthenticated) {
        setCartCount(0);
        return;
      }

      const res = await api.get("/customer/cart/count");
      setCartCount(Number(res.data?.count || 0));
    } catch (error) {
      console.error("Cart count fetch failed:", error);
      setCartCount(0);
    }
  };

  const fetchNotificationCount = async () => {
    try {
      if (!isAuthenticated) {
        setNotificationCount(0);
        return;
      }

      const res = await api.get("/customer/notifications/unread-count");

      setNotificationCount(Number(res.data?.count || 0));
    } catch (error) {
      console.error("Notification count fetch failed:", error);
      setNotificationCount(0);
    }
  };

  const fetchWishlistCount = async () => {
    try {
      if (!isAuthenticated) {
        setWishlistCount(0);
        return;
      }

      const res = await api.get("/customer/wishlist/count");

      setWishlistCount(
        Number(res.data?.data?.count || res.data?.count || 0)
      );
    } catch (error) {
      console.error("Wishlist count fetch failed:", error);
      setWishlistCount(0);
    }
  };

  useEffect(() => {
    fetchCartCount();
  }, [isAuthenticated, location.pathname]);

  useEffect(() => {
    fetchNotificationCount();

    const interval = setInterval(fetchNotificationCount, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated, location.pathname]);


  useEffect(() => {
    fetchWishlistCount();
  }, [isAuthenticated, location.pathname]);

  useEffect(() => {
    const handleCartUpdated = () => {
      fetchCartCount();
    };

    window.addEventListener("cart-updated", handleCartUpdated);

    return () => {
      window.removeEventListener("cart-updated", handleCartUpdated);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const handleWishlistUpdated = () => {
      fetchWishlistCount();
    };

    window.addEventListener(
      "wishlist-updated",
      handleWishlistUpdated
    );

    return () => {
      window.removeEventListener(
        "wishlist-updated",
        handleWishlistUpdated
      );
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollFrameRef.current !== null) return;

      scrollFrameRef.current = window.requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        const shouldShow =
          currentScrollY < 50 || currentScrollY < lastScrollYRef.current;

        setShowHeader((currentValue) =>
          currentValue === shouldShow ? currentValue : shouldShow
        );
        lastScrollYRef.current = currentScrollY;
        scrollFrameRef.current = null;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSearchKeyword(params.get("search") || "");
  }, [location.search]);

  const handleLogout = async () => {
    setShowSidebar(false);
    setOpenMenu(null);
    setOpenSubMenu(null);

    await logout();

    setCartCount(0);
    setNotificationCount(0);
    setWishlistCount(0);

    navigate("/login", {
      replace: true,
    });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();

    const keyword = searchKeyword.trim();

    if (!keyword) {
      navigate("/shop");
      return;
    }

    navigate(`/shop?search=${encodeURIComponent(keyword)}`);
  };

  useEffect(() => {
    setShowSidebar(false);
    setOpenMenu(null);
    setOpenSubMenu(null);
  }, [location.pathname, location.search]);

  return (
    <div className={`main-header ${showHeader ? "show" : "hide"}`}>
      <div className="topbar">
        <div className="container d-flex justify-content-end px-4 py-1">
          {!authLoading && !isAuthenticated ? (
            <span
              onClick={() => navigate("/login")}
              style={{ cursor: "pointer" }}
            >
              Register or Sign in
            </span>
          ) : (
            <span>Welcome, {user?.name}</span>
          )}

          <span className="px-2">|</span>
          <span>INR</span>
        </div>
      </div>

      <div className="container d-flex align-items-center justify-content-between py-3 mainNav">
        <div onClick={() => navigate("/")} style={{ cursor: "pointer" }} className="ps-5 ps-lg-0">
          <img
            src={logo}
            alt="Wedding Stationery"
            className="logo"
            width="131"
            height="150"
            decoding="async"
          />
        </div>

        <form className="d-flex w-50 order-lg-2" onSubmit={handleSearchSubmit}>
          <input
            type="text"
            placeholder="Search products..."
            className="form-control"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />

          <button type="submit" className="btn btn-dark ms-2">
            SEARCH
          </button>
        </form>

        <div className="d-flex gap-3 align-items-center order-lg-3">
          {!authLoading && isAuthenticated && (
            <div
              onClick={() => navigate("/wishlist")}
              style={{
                cursor: "pointer",
                position: "relative",
              }}
            >
              <LuHeart size={20} />

              {wishlistCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: "-5px",
                    right: "-7px",
                    background: "#000",
                    color: "#fff",
                    borderRadius: "999px",
                    minWidth: "16px",
                    height: "16px",
                    fontSize: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 4px",
                  }}
                >
                  {wishlistCount > 99 ? "99+" : wishlistCount}
                </span>
              )}
            </div>
          )}
          {!authLoading && isAuthenticated && (
            <div
              onClick={() => navigate("/notifications")}
              style={{
                cursor: "pointer",
                position: "relative",
              }}
            >
              <LuBell size={20} />

              {notificationCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: "-5px",
                    right: "-7px",
                    background: "#000",
                    color: "#fff",
                    borderRadius: "999px",
                    minWidth: "16px",
                    height: "16px",
                    fontSize: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 4px",
                  }}
                >
                  {notificationCount > 99 ? "99+" : notificationCount}
                </span>
              )}
            </div>
          )}

          {!authLoading && isAuthenticated && (
            <div
              onClick={() => navigate("/cart")}
              style={{ cursor: "pointer" }}
              className="d-flex align-items-center gap-1 position-relative"
            >
              <LuShoppingBag size={20} />
              <span
                style={{
                  position: "absolute",
                  top: "-8px",
                  right: "-10px",
                  background: "#000",
                  color: "#fff",
                  borderRadius: "999px",
                  minWidth: "16px",
                  height: "16px",
                  fontSize: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 4px",
                }}
              >
                {cartCount}
              </span>
            </div>
          )}

          {!authLoading && !isAuthenticated ? (
            // <button
            //   type="button"
            //   className="login-header-btn"
            //   onClick={() => navigate("/login")}
            // >
            //   Login
            // </button>
            ''
          ) : (
            !authLoading &&
            isAuthenticated && (
              <div className="user-menu">
                <div className="user-trigger">
                  <img
                    src={user?.image_url || userImage}
                    alt="user"
                    className="user-avatar"
                  />

                  <div className="user-info">
                    <strong>
                      {user?.name?.length > 7
                        ? user.name.substring(0, 7) + "..."
                        : user?.name}
                    </strong>
                  </div>

                  <RiArrowDropDownLine size={22} />
                </div>

                <div className="user-dropdown">
                  {/* <div className="user-head">
                    <img
                      src={user?.image_url || userImage}
                      alt="user"
                      className="dropdown-avatar"
                    />

                    <div>
                      <h6>{user?.name || userName}</h6>
                      <p>{user?.email || userEmail}</p>
                    </div>
                  </div>

                  <div className="dropdown-line" /> */}

                  <div className="user-drop-item" onClick={() => navigate("/profile")}>
                    <LuUser />
                    <span>My Account</span>
                  </div>

                  <div className="user-drop-item" onClick={() => navigate("/orders")}>
                    <LuShoppingBag />
                    <span>My Orders</span>
                  </div>

                  <div className="user-drop-item" onClick={() => navigate("/disputes")}>
                    <FiHelpCircle />
                    <span>Help Center</span>
                  </div>

                  <div className="dropdown-line" />

                  <div className="user-drop-item logout" onClick={handleLogout}>
                    <LuLogOut />
                    <span>Log Out</span>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>

      <Navbar expand="lg" className="border-lg-top border-lg-bottom resphide_height">
        <Container>
          <Navbar.Toggle onClick={() => setShowSidebar(true)} />

          <Navbar.Collapse className="justify-content-center resphide">
            <Nav className="gap-4 text-uppercase fw-semibold">
              <Nav.Link
                onClick={() => navigate("/")}
                className={location.pathname === "/" ? "active-menu" : ""}
              >
                Home
              </Nav.Link>

              <Nav.Link
                onClick={() => navigate("/shop")}
                className={
                  location.pathname === "/shop" ? "active-menu" : ""
                }
              >
                Shop
              </Nav.Link>

              {/* <NavDropdown
                title={
                  <span className="d-flex align-items-center">
                    Shop Wedding <RiArrowDropDownLine size={20} />
                  </span>
                }
                className={`custom-dropdown ${location.pathname.includes("/shop")
                  ? "active-menu"
                  : ""
                  }`}
              >
                <div className="dropdown-item-wrapper">
                  <span onClick={() => navigate("/shop")}>
                    Wedding Cards
                  </span>
                </div>

                <div className="dropdown-item-wrapper has-child">
                  <span className="d-flex align-items-center">
                    By Occasion <MdOutlineNavigateNext />
                  </span>

                  <div className="submenu">
                    <div onClick={() => navigate("/shop-wedding/hindu")}>
                      Hindu Wedding
                    </div>

                    <div onClick={() => navigate("/shop-wedding/muslim")}>
                      Muslim Wedding
                    </div>

                    <div onClick={() => navigate("/shop-wedding/christian")}>
                      Christian Wedding
                    </div>
                  </div>
                </div>
              </NavDropdown> */}

              {/* <Nav.Link
                onClick={() => navigate("/about-us")}
                className={
                  location.pathname === "/about-us" ? "active-menu" : ""
                }
              >
                About Us
              </Nav.Link> */}

              {/* <Nav.Link
                onClick={() => navigate("/srishbish-mini")}
                className={
                  location.pathname === "/srishbish-mini" ? "active-menu" : ""
                }
              >
                Srishbish Mini
              </Nav.Link> */}

              <Nav.Link
                onClick={() => navigate("/faqs")}
                className={location.pathname === "/faqs" ? "active-menu" : ""}
              >
                FAQs
              </Nav.Link>



              <Nav.Link
                onClick={() => navigate("/contact-us")}
                className={
                  location.pathname === "/contact-us" ? "active-menu" : ""
                }
              >
                Contact Us
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Offcanvas
        show={showSidebar}
        onHide={() => setShowSidebar(false)}
        placement="start"
      >
        <Offcanvas.Header closeButton>
          <div onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
            <img
              src={logo}
              alt="Wedding Stationery"
              className="logo"
              width="131"
              height="150"
              decoding="async"
            />
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="mobile-sidebar-menu">

            <div onClick={() => handleMobileNavigate("/")}>
              <FiHome className="me-2" /> Home
            </div>

            {openMenu === "shop" && (
              <div className="ps-3">

                <div onClick={() => handleMobileNavigate("/shop")}>
                  <FiShoppingBag className="me-2" /> Wedding Cards
                </div>

                <div
                  onClick={() =>
                    setOpenSubMenu((prev) =>
                      prev === "occ" ? null : "occ"
                    )
                  }
                  className="d-flex justify-content-between"
                >
                  <span>
                    <FiShoppingBag className="me-2" /> By Occasion
                  </span>

                  <span>
                    {openSubMenu === "occ" ? (
                      <FiChevronDown />
                    ) : (
                      <FiChevronRight />
                    )}
                  </span>
                </div>

                {openSubMenu === "occ" && (
                  <div className="ps-3">
                    <div onClick={() => handleMobileNavigate("/shop-wedding/hindu")}>
                      Hindu Wedding
                    </div>

                    <div onClick={() => handleMobileNavigate("/shop-wedding/muslim")}>
                      Muslim Wedding
                    </div>

                    <div onClick={() => handleMobileNavigate("/shop-wedding/christian")}>
                      Christian Wedding
                    </div>
                  </div>
                )}
              </div>
            )}

            <div onClick={() => handleMobileNavigate("/shop")}>
              <FiShoppingBag className="me-2" /> Shop
            </div>

            {/* <div onClick={() => handleMobileNavigate("/about-us")}>
              <FiInfo className="me-2" /> About Us
            </div> */}

            <div onClick={() => handleMobileNavigate("/faqs")}>
              <FiHelpCircle className="me-2" /> FAQs
            </div>

            <div onClick={() => handleMobileNavigate("/contact-us")}>
              <FiPhone className="me-2" /> Contact Us
            </div>

            <hr />

            {!authLoading && !isAuthenticated ? (
              <div onClick={() => handleMobileNavigate("/login")}>
                <FiLogIn className="me-2" /> Login
              </div>
            ) : (
              <>
                <div onClick={() => handleMobileNavigate("/profile")}>
                  <FiUser className="me-2" /> My Account
                </div>

                <div onClick={() => handleMobileNavigate("/orders")}>
                  <FiShoppingBag className="me-2" /> My Orders
                </div>

                <div onClick={() => handleMobileNavigate("/disputes")}>
                  <FiHelpCircle className="me-2" /> Help Center
                </div>

                <div
                  onClick={async () => {
                    setShowSidebar(false);
                    setOpenMenu(null);
                    setOpenSubMenu(null);
                    await handleLogout();
                  }}
                >
                  <FiLogOut className="me-2" /> Logout
                </div>
              </>
            )}
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default Header;
